import { TextInput, Dropdown } from '@mgmresorts/mgm-ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useContent } from '../../../../hooks/useContent';
import { BindInput } from '../../../../hooks/useInput';
import { BindSelect } from '../../../../hooks/useSelect';
import { useFieldErrorAnalytics } from '../../../../pages/SignUp/useFieldErrorAnalytics';
import { countries, states } from '../../../../utils/address';
import { MaxLengths } from '../../../../utils/constants';
import { isValidZip } from '../../../../utils/validators';

import { StyledContainer } from './styles';

type ManualAddressFormProps = {
  bindAddress: BindInput;
  bindAddress2: BindInput;
  bindCity: BindInput;
  bindZip: BindInput;
  bindCountry: BindSelect;
  bindState: BindSelect;
  resetState: () => void;
  bindCustomState: BindInput;
  resetCustomState: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  invalidFields: string[];
  removeInvalidField?: (identifier: string) => void;
  setInvalidField?: (identifier: string[]) => void;
};

export function ManualAddressForm(props: ManualAddressFormProps) {
  const {
    bindAddress,
    bindAddress2,
    bindCity,
    bindZip,
    bindState,
    resetState,
    bindCountry,
    bindCustomState,
    resetCustomState,
    isLoading,
    invalidFields,
    disabled,
    removeInvalidField,
    setInvalidField,
  } = props;
  const { getContentByPath } = useContent();
  const [errorZip, setErrorZip] = useState('');

  const errorAddress = useMemo((): string | undefined => {
    if (bindAddress.value && bindAddress.value.length > MaxLengths.Address) {
      return `${getContentByPath('shared.fields.address.address.errorLength')} (${bindAddress.value.length})`;
    }
    if (invalidFields.includes('address')) {
      return getContentByPath('shared.fields.address.address.error');
    }
    return undefined;
  }, [bindAddress.value, getContentByPath, invalidFields]);

  const errorCity = useMemo(
    (): string | undefined =>
      invalidFields.includes('city') ? getContentByPath('shared.fields.address.city.error') : undefined,
    [getContentByPath, invalidFields],
  );

  const stateOptions = useMemo(
    () => states[(bindCountry?.value || 'US') as keyof typeof states]?.map(value => ({ value, text: value })) ?? [],
    [bindCountry?.value],
  );

  const errorState = useMemo(
    (): string | undefined =>
      invalidFields.includes('state') ? getContentByPath('shared.fields.address.state.error') : undefined,
    [getContentByPath, invalidFields],
  );

  const errorCustomState = useMemo(
    (): string | undefined =>
      invalidFields.includes('customState') ? getContentByPath('shared.fields.address.state.error') : undefined,
    [getContentByPath, invalidFields],
  );

  const handleStateChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event?.target.value;
      if (value) {
        resetCustomState();
        if (removeInvalidField) {
          removeInvalidField('state');
        }
      }
      bindState.onChange(value);
    },
    [bindState, resetCustomState, removeInvalidField],
  );

  const handleCustomStateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value) {
        resetState();
      }
      bindCustomState.onChange(event);
    },
    [bindCustomState, resetState],
  );

  const validateZip = useCallback(
    (country, zip) => {
      if (!isValidZip(country, zip)) {
        setErrorZip(getContentByPath(`shared.fields.address.zip.${zip ? 'error' : 'empty'}`));
      } else {
        setErrorZip('');
      }
    },
    [getContentByPath],
  );

  const handleZipChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      bindZip.onChange(event);
      validateZip(bindCountry.value, event.target.value);
    },
    [bindZip, bindCountry, validateZip],
  );

  const handleCountryChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event?.target.value;
      bindCountry.onChange(value);
      validateZip(value, bindZip?.value);
      const isUSorCA = ['US', 'CA'].includes(value);
      if (isUSorCA) {
        setInvalidField && setInvalidField([...invalidFields, 'state']);
        removeInvalidField && removeInvalidField('customState');
      } else {
        removeInvalidField && removeInvalidField('state');
        removeInvalidField && removeInvalidField('customState');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bindCountry, bindZip, validateZip, bindCustomState],
  );

  const countryOptions = useMemo(() => Object.entries(countries).map(([value, label]) => ({ value, text: label })), []);

  useEffect(() => {
    // When the list of possible states changes, if the selected state is no longer available, clear the selection
    if (bindState?.value && !stateOptions.find(({ value }) => value === bindState?.value)) {
      bindState?.onChange('');
    }
  }, [stateOptions, bindState]);

  useFieldErrorAnalytics({
    field: 'address',
    invalidFields,
    contentPathRoot: 'shared.fields.address.address',
  });

  useFieldErrorAnalytics({
    field: 'city',
    invalidFields,
    contentPathRoot: 'shared.fields.address.city',
  });

  useFieldErrorAnalytics({
    field: 'state',
    invalidFields,
    contentPathRoot: 'shared.fields.address.state',
  });

  useFieldErrorAnalytics({
    field: 'customState',
    invalidFields,
    contentPathRoot: 'shared.fields.address.state',
  });

  useFieldErrorAnalytics({
    field: 'zip',
    invalidFields: errorZip ? ['zip'] : [],
    contentPathRoot: 'shared.fields.address.zip',
  });

  return (
    <StyledContainer>
      <Dropdown
        id='country'
        data-testid='country'
        placeholder={getContentByPath('shared.fields.address.country.title')}
        options={countryOptions}
        disabled={isLoading || disabled}
        {...bindCountry}
        onChange={handleCountryChange}
        autoComplete='country-name'
      />
      <TextInput
        variant='default'
        label={getContentByPath('shared.fields.address.address.title')}
        id='address'
        testId='address'
        disabled={isLoading || disabled}
        error={!!errorAddress}
        hint={errorAddress}
        {...bindAddress}
        autoComplete='address-line1'
      />
      <TextInput
        variant='default'
        label={getContentByPath('shared.fields.address.address2.title')}
        id='address2'
        testId='address2'
        disabled={isLoading || disabled}
        {...bindAddress2}
        autoComplete='address-line2'
      />
      <TextInput
        variant='default'
        label={getContentByPath('shared.fields.address.city.title')}
        id='city'
        testId='city'
        error={!!errorCity}
        hint={errorCity}
        disabled={isLoading || disabled}
        {...bindCity}
        autoComplete='address-level2'
      />
      {stateOptions.length > 0 ? (
        <Dropdown
          id='state'
          data-testid='state'
          placeholder={getContentByPath('shared.fields.address.state.title')}
          options={stateOptions}
          disabled={isLoading || disabled}
          maxHeight={300}
          {...bindState}
          onChange={handleStateChange}
          error={!!errorState}
          hint={errorState}
          autoComplete='address-level1'
        />
      ) : (
        <TextInput
          variant='default'
          label={getContentByPath('shared.fields.address.state.title')}
          id='state'
          testId='custom-state'
          disabled={isLoading || disabled}
          {...bindCustomState}
          onChange={handleCustomStateChange}
          error={!!errorCustomState}
          hint={errorCustomState}
          autoComplete='address-level1'
        />
      )}
      <TextInput
        variant='default'
        label={getContentByPath('shared.fields.address.zip.title')}
        id='zip'
        testId='zip'
        disabled={isLoading || disabled}
        error={!!errorZip}
        hint={errorZip}
        {...bindZip}
        onChange={handleZipChange}
        onBlur={handleZipChange}
        autoComplete='postal-code'
      />
    </StyledContainer>
  );
}
